import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { cn, Price } from '@divlab/divanui';
import { IconCart, IconCheck } from '@divlab/divanui/icons';
import { CSSTransition } from 'react-transition-group';

import * as Cart from '@Queries/Cart';
import * as PickPairStore from '@Stores/PickPair';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import { useMeta } from '@Queries/Meta';
import useSearchParams from '@Hooks/useSearchParams';
import useNavigation from '@Navigation/useNavigation';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './StickyBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';

export interface StickyBuyButtonProps extends ButtonProps {
  className?: string;
  visible?: boolean;
  onClick?: () => void;
}

const StickyBuyButton: FC<StickyBuyButtonProps> = (props) => {
  const { className, visible = false, onClick, ...restProps } = props;
  const page = PageProductStore.useData();
  const updateSet = Cart.useUpdatePositionInCart();
  const { t } = useTranslation();
  const parameterValues = PageProductStore.getParameterValues();
  const shopProducts = PickPairStore.useSelectedSet();
  const [open, setOpen] = useState(false);
  const hasWarnings = PickPairStore.useHasWarnings();
  const searchParams = useSearchParams();
  const oldHash = searchParams.get('hash');
  const meta = useMeta();
  const { region } = meta.data;
  const { openPage } = useNavigation();
  const addToCart = Cart.useAddToCart();

  const shopProduct = useMemo(() => {
    return {
      shopProducts,
      shopProductId: page.product.id,
      parameterValues,
    };
  }, [page.product.id, parameterValues, shopProducts]);

  const hasInCart = Cart.useHasInCart(shopProduct);

  const handleError = useCallback(() => {
    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  }, []);

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    if (hasWarnings) return;

    if (hasInCart) {
      openPage({ url: `${region.url}/order/check` });
      return;
    }

    const products = [
      {
        isModular: page.isModular,
        ...shopProduct,
      },
    ];

    if (oldHash) {
      if (!updateSet.isPending && !updateSet.isSuccess) {
        updateSet.mutate(
          { products, options: { isRelated: false }, oldHash },
          {
            onError: handleError,
            onSuccess: () => openPage({ url: `${region.url}/order/check` }),
          },
        );
      }
      return;
    }

    if (addToCart.isPending || addToCart.isError) {
      return;
    }

    addToCart.mutate(
      {
        products,
        options: { isRelated: false },
        analyticsData: {
          products: [page.product],
        },
      },
      {
        onError: handleError,
      },
    );

    // ModalWindows.open('Cart', { products });
  }, [
    onClick,
    hasWarnings,
    hasInCart,
    page.isModular,
    page.product,
    shopProduct,
    oldHash,
    addToCart,
    handleError,
    openPage,
    region.url,
    updateSet,
  ]);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  return (
    <CSSTransition
      timeout={400}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={open}
    >
      <Button
        {...restProps}
        wide
        size='l'
        className={cn(
          styles.buyButton,
          styles.sizeBig,
          { [styles.hasInCart]: hasInCart },
          className,
        )}
        onClick={handleClick}
        theme={hasInCart ? 'transparent' : 'primary'}
        waiting={addToCart.isPending || updateSet.isPending}
        data-testid='mobile-buy-button'
      >
        {hasInCart ? (
          <IconCheck className={styles.iconCheck} />
        ) : (
          !oldHash && <IconCart className={styles.iconBasket} />
        )}

        {t(hasInCart ? 'ui.in-cart-short' : oldHash ? 'ui.save-changes' : 'ui.add-to-cart')}

        <Price className={styles.price} price={page.product.price.actual} />
      </Button>
    </CSSTransition>
  );
};

export default memo(StickyBuyButton);
